import React from 'react';
import { DatePicker, Input, InputNumber, Radio } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import BaseForm from '../../../../components/form/BaseForm';
import { ShiftMode } from '../../../../lib/api/settlementCompleteDateAdjustment';

type Props = {
  shiftMode: ShiftMode;
  deltaDays: number;
  targetCompleteDate?: string;
  reason: string;
  onShiftModeChange: (mode: ShiftMode) => void;
  onDeltaDaysChange: (v: number) => void;
  onTargetCompleteDateChange: (v?: string) => void;
  onReasonChange: (v: string) => void;
};

/**
 * 변경 조건 (이동 방식 + 사유) 입력 폼.
 *
 * 정산 리스트 페이지의 SettlementListSearchBox 안에서 쓰는 BaseForm(label/content)
 * 레이아웃을 그대로 따라 시각적 통일을 맞춘다.
 *
 * 기준일 변경(SettlementDateAdjustment) 폼과의 차이점:
 *  - 변경 대상 필드명이 `completeDate` (단일).
 *  - 운영 시나리오상 절대 날짜 지정이 더 흔하므로 default mode 는 `absolute`.
 *  - 완료일은 "이미 일어난 정산"의 회계 일자이므로 미래 날짜를 막는다 (`disabledDate`).
 */
export const ShiftForm = ({
  shiftMode,
  deltaDays,
  targetCompleteDate,
  reason,
  onShiftModeChange,
  onDeltaDaysChange,
  onTargetCompleteDateChange,
  onReasonChange,
}: Props): React.JSX.Element => {
  return (
    <>
      <BaseForm
        required
        label="이동 방식"
        content={
          <Radio.Group
            value={shiftMode}
            onChange={(e) => onShiftModeChange(e.target.value as ShiftMode)}
          >
            <Radio value="absolute">특정 날짜로 고정</Radio>
            <Radio value="delta">일수 가감 (+N일)</Radio>
          </Radio.Group>
        }
      />

      {shiftMode === 'absolute' ? (
        <BaseForm
          required
          label="목표 정산 완료일"
          content={
            <DatePicker
              value={targetCompleteDate ? dayjs(targetCompleteDate) : null}
              onChange={(d) => onTargetCompleteDateChange(d?.format('YYYY-MM-DD'))}
              // 완료일은 과거-현재 시점만 허용. 회계 일자라 미래 지정이 의미 없음.
              disabledDate={(d: Dayjs) => d.isAfter(dayjs(), 'day')}
              style={{ width: 200 }}
            />
          }
        />
      ) : (
        <BaseForm
          required
          label="이동 일수"
          content={
            <InputNumber
              min={-60}
              max={60}
              value={deltaDays}
              onChange={(v) => onDeltaDaysChange(v ?? 0)}
              addonAfter="일"
              style={{ width: 160 }}
            />
          }
        />
      )}

      <BaseForm
        required
        label="변경 사유"
        content={
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="변경 사유를 입력해주세요"
            maxLength={500}
            showCount
          />
        }
      />
    </>
  );
};
